import {
  AutocompleteInputChangeReason,
  debounce,
  Grid,
  Typography,
} from "@mui/material";
import { SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomAutocompleteMulti from "../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import FormTable, {
  DataItem,
} from "../../../../common/components/functional/formTable/FormTable";
import { LanguageSkillEdit } from "../../../../common/components/functional/languageSkillEdit/LanguageSkillEdit";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import useLanguageSkills from "../../../../hooks/useLanguageSkills";
import { ContentKey, EditMode } from "../../../talentProfile/TalentDetails";
import { LanguageSkillCode } from "../../../talents/talentAPI";
import { LanguageSkill } from "../../orderTypes";

interface Options {
  certificates: AutocompleteType[];
  languages: LanguageSkill[];
  trainings: AutocompleteType[];
}

type Props = {
  disableEdit: boolean;
  certificates: AutocompleteType[];
  languages: LanguageSkill[];
  trainings: AutocompleteType[];
  onRequirementsChange: (
    certificates: AutocompleteType[],
    trainings: AutocompleteType[],
    languages: LanguageSkill[],
  ) => void;
};

const initialSearchTerms = {
  certificates: "",
  trainings: "",
  languages: "",
};

export const Requirements = ({
  disableEdit,
  certificates,
  languages: languageSkills,
  trainings,
  onRequirementsChange,
}: Props) => {
  const { t } = useTranslation(["order", "skills", "certificates"]);

  const { getLanguageSkillsNameByCode } = useLanguageSkills();

  const isNewOrder = false;
  const [defaultValues, setDefaultValues] = useState<Options>({
    certificates: certificates,
    languages: languageSkills,
    trainings: trainings,
  });
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageSkill>();
  const existingLanguageCodes =
    languageSkills.map((language) => language.languageCode) || [];

  const [editMode, setEditMode] = useState<EditMode>({
    enabled: false,
    content: null,
    id: "",
  });

  const formattedData: DataItem[] = languageSkills
    .map((language) => ({
      values: {
        language: getLanguageSkillsNameByCode(language.languageCode),
        level: t("languageSkill:" + language.skillLevel),
      },
      id: language.id,
      deletable: true,
    }))
    .sort((a, b) => a.values.language.localeCompare(b.values.language));

  const headers = [
    { text: t("languageSkill:language") },
    { text: t("languageSkill:level") },
  ];
  const addButtonLabel = t("languageSkill:add");

  const onChange = (
    e: SyntheticEvent<Element, Event>,
    newValue: AutocompleteType[] | null,
    name: string,
  ) => {
    if (newValue) {
      const requirements = {
        certificates,
        trainings,
        languageSkills,
        [name]: newValue,
      };
      onRequirementsChange(
        requirements.certificates,
        requirements.trainings,
        requirements.languageSkills,
      );
    }
  };

  const handleOpenEditModal = (content: ContentKey, id: string = "") => {
    setEditMode({ enabled: true, content, id });
    setSelectedLanguage(languageSkills.find((language) => language.id === id));
  };

  const handleCloseEditModal = () => {
    setEditMode({ enabled: false, content: null, id: "" });
    setSelectedLanguage(undefined);
  };

  const onChangeLanguageSkillLevel = (
    languageCode: LanguageSkillCode,
    level: string,
  ) => {
    const editedLanguages = languageSkills.map((language) =>
      language.languageCode === languageCode
        ? { ...language, skillLevel: level }
        : language,
    );
    onRequirementsChange(certificates, trainings, editedLanguages);
    handleCloseEditModal();
  };

  const deleteLanguage = (id: string) => {
    const editedLanguages = languageSkills.filter(
      (language) => language.id !== id,
    );
    onRequirementsChange(certificates, trainings, editedLanguages);
  };

  const onInputChange = debounce(
    (key: string, term: string, reason?: AutocompleteInputChangeReason) => {
      setSearchTerms({ ...searchTerms, [key]: term });

      if (!term) {
        setDefaultValues({ ...defaultValues, [key]: [] });
      }
    },
    400,
  );

  return (
    <>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:cardsAndCerts")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <CustomAutocompleteMulti
          disabled={disableEdit}
          options={defaultValues.certificates}
          fullWidth
          name="certificates"
          onChange={onChange}
          onInputChange={onInputChange}
          defaultValue={defaultValues.certificates}
          onOpen={() => {}}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:trainings")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <CustomAutocompleteMulti
          disabled={disableEdit}
          options={defaultValues.trainings}
          fullWidth
          name="trainings"
          onChange={onChange}
          onInputChange={onInputChange}
          defaultValue={defaultValues.trainings}
          onOpen={() => {}}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">
          {t("common:languageSkills")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <FormTable
          data={formattedData}
          headers={headers}
          handleOpenEditModal={handleOpenEditModal}
          contentKey="languages"
          disableEditRow={disableEdit}
          handleDelete={disableEdit ? undefined : deleteLanguage}
          addButtonLabel={isNewOrder ? addButtonLabel : undefined}
        />
        <DialogComponent open={editMode.enabled} onClose={handleCloseEditModal}>
          <LanguageSkillEdit
            selectedLanguage={selectedLanguage}
            disabledLanguageCodes={existingLanguageCodes}
            onSaveChanges={onChangeLanguageSkillLevel}
            onClose={handleCloseEditModal}
          />
        </DialogComponent>
      </Grid>
    </>
  );
};
